import { motion } from 'framer-motion';
import { useHealth, useJwks } from '@/api/hooks';
import { MaterialIcon } from '@/components/MaterialIcon';
import { StatusBadge } from '@/components/StatusBadge';
import { CodeBlock } from '@/components/CodeBlock';
import { FlowStep } from '@/components/FlowStep';
import { cn } from '@/utils/classnames';

/**
 * Screen 5 — read-only view of the OIDC provider surface.
 *
 * Source of truth for endpoints / supported features:
 *   src/http.rs::build_router
 *   src/oidc.rs::discovery
 *   migrations/002_seed_test_client.sql (test client seed)
 *   src/oidc.rs::IdTokenClaims (real emitted claim fields)
 */
export function OidcProviderPage() {
  const { data: health } = useHealth();
  const { data: jwks, isPending: jwksPending } = useJwks();
  const issuer = health?.issuer ?? '—';
  const jwksEmpty = !jwksPending && (jwks?.keys.length ?? 0) === 0;

  return (
    <div className="flex flex-col gap-panel-gap">
      <Header issuer={issuer} />

      <div className="grid grid-cols-12 gap-panel-gap">
        {/* Left column */}
        <div className="col-span-12 flex flex-col gap-panel-gap xl:col-span-7">
          <EndpointsCard jwksPending={jwksPending} jwksEmpty={jwksEmpty} />
          <div className="grid grid-cols-1 gap-panel-gap md:grid-cols-2">
            <SupportedFeaturesCard />
            <TestClientCard />
          </div>
        </div>

        {/* Right column */}
        <div className="col-span-12 flex flex-col gap-panel-gap xl:col-span-5">
          <FlowCard />
          <IdTokenClaimsCard issuer={issuer} />
        </div>
      </div>
    </div>
  );
}

// ─── Header ────────────────────────────────────────────────────────────────

function Header({ issuer }: { issuer: string }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.22, ease: 'easeOut' }}
      className="flex flex-wrap items-end justify-between gap-stack-md"
    >
      <div>
        <h1 className="font-headline-lg text-headline-lg font-bold text-on-surface">
          OIDC Provider
        </h1>
        <p className="mt-unit max-w-3xl font-body-md text-body-md text-on-surface-variant">
          飞书身份源 → Cbwee OIDC Issuer → 业务应用。RS256 ID Token 由
          {' '}<code className="font-code-md text-code-md text-on-surface">CBWEE_IDENTITY__OIDC_SIGNING_KEY_PATH</code>{' '}
          加载的私钥签发。
        </p>
      </div>
      <motion.span
        layoutId="issuer-pill"
        transition={{ type: 'spring', stiffness: 380, damping: 32 }}
        title={issuer}
        className="inline-flex max-w-full items-center gap-unit truncate rounded border border-outline-variant bg-surface-container-lowest px-stack-sm py-unit font-code-md text-code-md text-primary"
      >
        <MaterialIcon name="public" size={14} />
        {issuer}
      </motion.span>
    </motion.div>
  );
}

// ─── Endpoints table ───────────────────────────────────────────────────────

interface EndpointRow {
  method: 'GET' | 'POST' | 'GET / POST';
  path: string;
  auth: { label: string; variant: 'success' | 'info' | 'warning' };
  /** When true, this row's status flips with JWKS state. */
  isJwks?: boolean;
}

const ENDPOINT_ROWS: EndpointRow[] = [
  {
    method: 'GET',
    path: '/.well-known/openid-configuration',
    auth: { label: 'public', variant: 'success' },
  },
  {
    method: 'GET',
    path: '/.well-known/jwks.json',
    auth: { label: 'public', variant: 'success' },
    isJwks: true,
  },
  {
    method: 'GET',
    path: '/oauth2/authorize',
    auth: { label: 'session', variant: 'info' },
  },
  {
    method: 'POST',
    path: '/oauth2/token',
    auth: { label: 'client_secret', variant: 'warning' },
  },
  {
    method: 'GET / POST',
    path: '/oauth2/userinfo',
    auth: { label: 'Bearer access_token', variant: 'warning' },
  },
];

function EndpointsCard({
  jwksPending,
  jwksEmpty,
}: {
  jwksPending: boolean;
  jwksEmpty: boolean;
}) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.22, ease: 'easeOut' }}
      className="overflow-hidden rounded border border-[#DFE1E6] bg-surface-container-lowest"
    >
      <header className="flex items-center gap-stack-sm border-b border-[#DFE1E6] bg-[#F4F5F7] px-container-padding py-stack-sm">
        <MaterialIcon name="api" size={18} className="text-on-surface-variant" />
        <h2 className="font-label-caps text-label-caps uppercase text-on-surface">
          Discovery & 公共端点
        </h2>
      </header>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-left">
          <thead>
            <tr className="border-b border-[#DFE1E6] bg-[#EBECF0]">
              <Th className="w-24">Method</Th>
              <Th>Path</Th>
              <Th className="w-44">Auth Required</Th>
              <Th className="w-28 text-center">Status</Th>
            </tr>
          </thead>
          <tbody>
            {ENDPOINT_ROWS.map((row) => (
              <tr
                key={row.path}
                className="border-b border-[#DFE1E6] transition-colors last:border-b-0 hover:bg-[#F4F5F7]"
              >
                <Td>
                  <MethodBadge method={row.method} />
                </Td>
                <Td>
                  <span className="font-code-md text-code-md text-on-surface">{row.path}</span>
                </Td>
                <Td>
                  <StatusBadge variant={row.auth.variant} dot>
                    {row.auth.label}
                  </StatusBadge>
                </Td>
                <Td className="text-center">
                  {row.isJwks ? (
                    jwksPending ? (
                      <StatusBadge variant="neutral">…</StatusBadge>
                    ) : jwksEmpty ? (
                      <StatusBadge variant="warning" dot>
                        JWKS empty
                      </StatusBadge>
                    ) : (
                      <StatusBadge variant="success" dot>
                        OK
                      </StatusBadge>
                    )
                  ) : (
                    <StatusBadge variant="success" dot>
                      OK
                    </StatusBadge>
                  )}
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </motion.section>
  );
}

function MethodBadge({ method }: { method: EndpointRow['method'] }) {
  let cls: string;
  if (method === 'GET') cls = 'bg-[#E3FCEF] text-[#006644] border-[#ABF5D1]';
  else if (method === 'POST') cls = 'bg-surface-container-high text-primary border-primary-fixed-dim';
  else cls = 'bg-surface-container text-on-surface border-outline-variant';
  return (
    <span
      className={cn(
        'inline-flex items-center rounded border px-stack-sm py-unit font-code-md text-code-md uppercase',
        cls,
      )}
    >
      {method}
    </span>
  );
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <th
      className={cn(
        'p-table-cell-padding font-label-caps text-label-caps uppercase text-on-surface-variant',
        className,
      )}
    >
      {children}
    </th>
  );
}

function Td({ children, className }: { children: React.ReactNode; className?: string }) {
  return <td className={cn('p-table-cell-padding align-middle', className)}>{children}</td>;
}

// ─── Supported features ────────────────────────────────────────────────────

const SUPPORTED_FEATURES: Array<{ key: string; values: string[] }> = [
  { key: 'response_types_supported', values: ['code'] },
  { key: 'grant_types_supported', values: ['authorization_code'] },
  { key: 'subject_types_supported', values: ['public'] },
  { key: 'id_token_signing_alg_values_supported', values: ['RS256'] },
  { key: 'scopes_supported', values: ['openid', 'profile', 'email'] },
  {
    key: 'claims_supported',
    values: [
      'sub',
      'name',
      'email',
      'picture',
      'cbwee_platform_roles',
      'cbwee_tenant_key',
      'cbwee_tenant_name',
    ],
  },
  {
    key: 'token_endpoint_auth_methods_supported',
    values: ['client_secret_post', 'client_secret_basic'],
  },
];

function SupportedFeaturesCard() {
  return (
    <motion.section
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.22, delay: 0.05, ease: 'easeOut' }}
      className="flex flex-col overflow-hidden rounded border border-[#DFE1E6] bg-surface-container-lowest"
    >
      <header className="border-b border-[#DFE1E6] bg-[#F4F5F7] px-container-padding py-stack-sm">
        <h2 className="font-label-caps text-label-caps uppercase text-on-surface">
          Supported Features
        </h2>
      </header>
      <div className="flex flex-1 flex-col gap-stack-md p-container-padding">
        {SUPPORTED_FEATURES.map((feature) => (
          <div key={feature.key}>
            <h3 className="mb-unit font-code-md text-code-md text-on-surface-variant">
              {feature.key}
            </h3>
            <div className="flex flex-wrap gap-stack-sm">
              {feature.values.map((value) => (
                <span
                  key={value}
                  className="rounded border border-outline-variant bg-surface-container px-stack-sm py-[2px] font-code-md text-code-md text-on-surface"
                >
                  {value}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </motion.section>
  );
}

// ─── Test client ───────────────────────────────────────────────────────────

const TEST_CLIENT = {
  client_id: 'cbwee-test-client',
  name: 'Cbwee Test Client',
  redirect_uris: ['http://localhost:18080/callback'],
};

function TestClientCard() {
  return (
    <motion.section
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.22, delay: 0.1, ease: 'easeOut' }}
      className="flex flex-col overflow-hidden rounded border border-[#DFE1E6] bg-surface-container-lowest"
    >
      <header className="border-b border-[#DFE1E6] bg-[#F4F5F7] px-container-padding py-stack-sm">
        <h2 className="font-label-caps text-label-caps uppercase text-on-surface">Test Client</h2>
      </header>
      <div className="flex flex-1 flex-col gap-stack-md p-container-padding">
        <Field label="client_id">
          <span className="truncate font-code-md text-code-md text-on-surface">
            {TEST_CLIENT.client_id}
          </span>
        </Field>
        <Field label="name">
          <span className="font-body-sm text-body-sm text-on-surface">{TEST_CLIENT.name}</span>
        </Field>
        <Field label="redirect_uris">
          <div className="flex flex-wrap gap-stack-sm">
            {TEST_CLIENT.redirect_uris.map((uri) => (
              <span
                key={uri}
                className="rounded border border-outline-variant bg-surface px-stack-sm py-unit font-code-md text-code-md text-on-surface"
              >
                {uri}
              </span>
            ))}
          </div>
        </Field>
        <Field label="client_secret">
          <div className="flex flex-col gap-unit">
            <StatusBadge variant="warning" dot>
              MASKED · hash stored only
            </StatusBadge>
            <p className="m-0 font-body-sm text-body-sm text-on-surface-variant">
              真实哈希通过{' '}
              <code className="font-code-md text-code-md text-on-surface">
                CBWEE_IDENTITY__TEST_OIDC_CLIENT_SECRET
              </code>{' '}
              在启动时 HMAC-SHA256 写入 oauth_clients.client_secret_hash 列。控制台永远不展示明文。
            </p>
          </div>
        </Field>
      </div>
    </motion.section>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="rounded border border-[#DFE1E6] bg-[#F4F5F7] p-stack-md">
      <label className="mb-unit block font-label-caps text-label-caps uppercase text-on-surface-variant">
        {label}
      </label>
      <div className="flex flex-col gap-unit">{children}</div>
    </div>
  );
}

// ─── Authorization-code flow ───────────────────────────────────────────────

function FlowCard() {
  return (
    <motion.section
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.22, ease: 'easeOut' }}
      className="overflow-hidden rounded border border-[#DFE1E6] bg-surface-container-lowest"
    >
      <header className="border-b border-[#DFE1E6] bg-[#F4F5F7] px-container-padding py-stack-sm">
        <h2 className="font-label-caps text-label-caps uppercase text-on-surface">
          Authorization Code Flow
        </h2>
      </header>
      <div className="flex flex-col p-container-padding">
        <FlowStep
          index={1}
          title="authorize"
          description={
            <>
              <span className="font-code-md text-code-md text-on-surface">GET /oauth2/authorize</span>
              <br />
              校验 client_id / redirect_uri / response_type=code，需要{' '}
              <code className="font-code-md text-code-md">cbwee_session</code> 会话。
            </>
          }
        />
        <FlowStep
          index={2}
          title="302 redirect"
          description={
            <>
              浏览器收到{' '}
              <code className="font-code-md text-code-md text-on-surface">302</code> +
              {' '}<code className="font-code-md text-code-md text-on-surface">?code=...</code>
              ，回到 client 注册的 redirect_uri。
            </>
          }
        />
        <FlowStep
          index={3}
          title="token"
          description={
            <>
              客户端{' '}
              <span className="font-code-md text-code-md text-on-surface">POST /oauth2/token</span>
              {' '}带 grant_type=authorization_code，验 client_secret，校验 code 未过期/未用，签发{' '}
              <code className="font-code-md text-code-md">RS256 id_token</code> +{' '}
              <code className="font-code-md text-code-md">access_token</code>。
            </>
          }
        />
        <FlowStep
          index={4}
          title="userinfo"
          description={
            <>
              客户端用{' '}
              <span className="font-code-md text-code-md text-on-surface">
                Authorization: Bearer access_token
              </span>{' '}
              调{' '}
              <code className="font-code-md text-code-md">/oauth2/userinfo</code> 取 sub / name /
              email / picture / cbwee_platform_roles / cbwee_tenant_*。
            </>
          }
          last
        />
      </div>
    </motion.section>
  );
}

// ─── ID Token claims preview ───────────────────────────────────────────────

function IdTokenClaimsCard({ issuer }: { issuer: string }) {
  // Always emit the canonical Cbwee issuer in the placeholder so the shape is
  // stable even when /healthz hasn't loaded yet. Use the live value when it
  // exists.
  const iss = issuer && issuer !== '—' ? issuer : 'https://identity.example.invalid';
  const claimsJson = JSON.stringify(
    {
      iss,
      sub: 'feishu:<union_id>',
      aud: 'cbwee-test-client',
      iat: 1700000000,
      exp: 1700003600,
      name: '张三',
      email: 'zhangsan@example.com',
      picture: 'https://.../avatar.png',
      cbwee_platform_roles: ['platform_admin'],
      cbwee_tenant_key: 'tenant_hash',
      cbwee_tenant_name: '示例租户',
    },
    null,
    2,
  );

  return (
    <motion.section
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.22, delay: 0.05, ease: 'easeOut' }}
      className="flex flex-1 flex-col overflow-hidden rounded border border-[#DFE1E6]"
    >
      <header className="flex items-center justify-between border-b border-outline/30 bg-[#14233D] px-container-padding py-stack-sm">
        <h2 className="flex items-center gap-unit font-label-caps text-label-caps uppercase text-inverse-on-surface">
          <MaterialIcon name="data_object" size={16} />
          ID Token Claims Preview
        </h2>
        <span className="font-label-caps text-label-caps uppercase text-outline-variant">
          占位值
        </span>
      </header>
      <CodeBlock variant="deep" className="rounded-none border-0 border-t-0">
        {claimsJson}
      </CodeBlock>
      <div className="border-t border-outline/30 bg-[#14233D] px-container-padding py-stack-sm">
        <p className="m-0 font-body-sm text-body-sm text-outline-variant">
          <code className="font-code-md text-code-md text-inverse-on-surface">
            cbwee_platform_roles
          </code>{' '}
          仅在{' '}
          <code className="font-code-md text-code-md text-inverse-on-surface">
            application_id == "__platform__"
          </code>{' '}
          时被填入 ID Token；
          <code className="font-code-md text-code-md text-inverse-on-surface">
            cbwee_tenant_key
          </code>{' '}
          /{' '}
          <code className="font-code-md text-code-md text-inverse-on-surface">
            cbwee_tenant_name
          </code>{' '}
          与 userinfo 保持同源（参见{' '}
          <code className="font-code-md text-code-md text-inverse-on-surface">
            src/oidc.rs::get_principal_claims
          </code>
          ）。
        </p>
      </div>
    </motion.section>
  );
}
